import { useState } from 'react';
import {
  Paper,
  Stack,
  Group,
  Button,
  Text,
  Modal,
  TextInput,
  Select,
  Textarea,
  ActionIcon,
  Card,
  Title,
  Alert,
  Center,
  Loader,
  Switch,
  Badge,
} from '@mantine/core';
import { IconPlus, IconTrash, IconEdit, IconAlertCircle, IconCheck, IconX } from '@tabler/icons-react';
import {
  usePromptTemplates,
  useCreatePromptTemplate,
  useUpdatePromptTemplate,
  useDeletePromptTemplate,
  PromptTemplate,
} from '../../api/promptTemplates';

const TEMPLATE_VARIABLES: Record<string, string[]> = {
  resume: ['masterTemplate', 'selectedContent', 'jobTitle', 'company', 'jobDescription'],
  assessment: ['resumeContent', 'jobDescription', 'jobTitle', 'company'],
  jobParsing: ['jobDescription'],
};

const DEFAULT_TEMPLATES: Record<string, string> = {
  resume: `You are an expert resume writer. Tailor the following resume content to match this job posting.

Job Title: {jobTitle}
Company: {company}

Job Description:
{jobDescription}

Master Resume Template:
{masterTemplate}

Additional Content to Consider:
{selectedContent}

Please generate a tailored resume that:
1. Highlights relevant skills and experience for this specific job
2. Uses keywords from the job description
3. Maintains professional formatting in Markdown
4. Keeps the same structure but personalizes the content
5. Makes it compelling for this specific role

Output only the tailored resume content in Markdown format.`,

  assessment: `Analyze how well the following resume matches this job description. Provide a structured assessment with:

1. Key Strengths (how resume matches job requirements)
2. Gap Analysis (missing skills/experience)
3. Recommendations (how to improve fit)
4. Overall Match Score (0-100%)

Resume:
{resumeContent}

Job Description:
{jobDescription}

Job Title: {jobTitle}
Company: {company}

Provide the assessment in a clear, organized format.`,

  jobParsing: `Extract structured data from this job posting and provide enhanced formatting.

Job Description:
{jobDescription}

Please provide:
1. Key responsibilities
2. Required skills
3. Preferred qualifications
4. Salary range (if available)
5. Remote/hybrid status (if available)
6. Any other important details

Format the output clearly and structured.`,
};

export function PromptTemplateManager() {
  const { data: templates = [], isLoading } = usePromptTemplates();
  const createMutation = useCreatePromptTemplate();
  const updateMutation = useUpdatePromptTemplate();
  const deleteMutation = useDeletePromptTemplate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [resultMessage, setResultMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const [formData, setFormData] = useState<Omit<PromptTemplate, 'id' | 'created_at' | 'updated_at'>>({
    name: '',
    template_type: 'resume',
    description: '',
    template_content: DEFAULT_TEMPLATES.resume,
    variables: TEMPLATE_VARIABLES.resume,
    is_default: false,
  });

  const handleOpenModal = (template?: PromptTemplate) => {
    if (template) {
      setEditingId(template.id);
      setFormData({
        name: template.name,
        template_type: template.template_type,
        description: template.description || '',
        template_content: template.template_content,
        variables: template.variables || [],
        is_default: template.is_default,
      });
    } else {
      setEditingId(null);
      setFormData({
        name: '',
        template_type: 'resume',
        description: '',
        template_content: DEFAULT_TEMPLATES.resume,
        variables: TEMPLATE_VARIABLES.resume,
        is_default: false,
      });
    }
    setResultMessage(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setResultMessage(null);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.template_content) {
      setResultMessage({
        type: 'error',
        message: 'Name and template content are required',
      });
      return;
    }

    try {
      if (editingId) {
        await updateMutation.mutateAsync({
          id: editingId,
          data: formData,
        });
        setResultMessage({
          type: 'success',
          message: 'Template updated successfully',
        });
      } else {
        await createMutation.mutateAsync(formData);
        setResultMessage({
          type: 'success',
          message: 'Template created successfully',
        });
      }
      setTimeout(handleCloseModal, 1500);
    } catch (error) {
      setResultMessage({
        type: 'error',
        message: error instanceof Error ? error.message : 'Operation failed',
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this template?')) {
      try {
        await deleteMutation.mutateAsync(id);
        setResultMessage({
          type: 'success',
          message: 'Template deleted successfully',
        });
      } catch (error) {
        setResultMessage({
          type: 'error',
          message: error instanceof Error ? error.message : 'Failed to delete template',
        });
      }
    }
  };

  const groupedByType = templates.reduce(
    (acc, template) => {
      if (!acc[template.template_type]) {
        acc[template.template_type] = [];
      }
      acc[template.template_type].push(template);
      return acc;
    },
    {} as Record<string, PromptTemplate[]>
  );

  return (
    <Paper p="lg" radius="md" withBorder>
      <Stack gap="md">
        <div>
          <Group justify="space-between" align="center">
            <div>
              <Title order={2} size="h3">
                Prompt Templates
              </Title>
              <Text size="sm" c="dimmed">
                Create and manage custom prompt templates for different artifact types
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={() => handleOpenModal()}>
              Create Template
            </Button>
          </Group>
        </div>

        {isLoading ? (
          <Center py="xl">
            <Loader />
          </Center>
        ) : templates.length === 0 ? (
          <Alert icon={<IconAlertCircle size={16} />} color="blue">
            No custom templates created yet. Create one to customize your resume generation prompts.
          </Alert>
        ) : (
          <Stack gap="md">
            {(['resume', 'assessment', 'jobParsing'] as const).map((type) => {
              const typeTemplates = groupedByType[type] || [];
              if (typeTemplates.length === 0) return null;

              return (
                <div key={type}>
                  <Title order={4} size="h5" mb="sm">
                    {type === 'resume' && 'Resume Generation'}
                    {type === 'assessment' && 'Assessment'}
                    {type === 'jobParsing' && 'Job Parsing'}
                  </Title>
                  <Stack gap="sm">
                    {typeTemplates.map((template) => (
                      <Card key={template.id} padding="sm" radius="md" withBorder>
                        <Card.Section p="sm" inheritPadding>
                          <Group justify="space-between" align="flex-start">
                            <div style={{ flex: 1 }}>
                              <Group gap="xs" mb="xs">
                                <div>
                                  <Text fw={500} size="sm">
                                    {template.name}
                                  </Text>
                                  {template.description && (
                                    <Text size="xs" c="dimmed">
                                      {template.description}
                                    </Text>
                                  )}
                                </div>
                                {template.is_default && <Badge color="blue" size="sm">Default</Badge>}
                              </Group>
                              <Text size="xs" c="dimmed" lineClamp={1}>
                                Variables: {template.variables?.join(', ') || 'None'}
                              </Text>
                            </div>

                            <Group gap="xs">
                              <Button
                                size="xs"
                                variant="default"
                                onClick={() => handleOpenModal(template)}
                              >
                                Edit
                              </Button>
                              <ActionIcon
                                color="red"
                                size="sm"
                                variant="light"
                                onClick={() => handleDelete(template.id)}
                              >
                                <IconTrash size={16} />
                              </ActionIcon>
                            </Group>
                          </Group>
                        </Card.Section>
                      </Card>
                    ))}
                  </Stack>
                </div>
              );
            })}
          </Stack>
        )}
      </Stack>

      {/* Template Editor Modal */}
      <Modal
        opened={isModalOpen}
        onClose={handleCloseModal}
        title={editingId ? 'Edit Template' : 'Create New Template'}
        size="lg"
      >
        <Stack gap="md">
          {resultMessage && (
            <Alert
              icon={resultMessage.type === 'success' ? <IconCheck size={16} /> : <IconX size={16} />}
              color={resultMessage.type === 'success' ? 'green' : 'red'}
            >
              {resultMessage.message}
            </Alert>
          )}

          {/* Name */}
          <TextInput
            label="Template Name"
            placeholder="e.g., Tailored Resume for Tech Roles"
            value={formData.name}
            onChange={(e) =>
              setFormData({
                ...formData,
                name: e.currentTarget.value,
              })
            }
          />

          {/* Template Type */}
          <Select
            label="Template Type"
            description="Choose what this template is used for"
            placeholder="Select type"
            data={[
              { value: 'resume', label: 'Resume Generation' },
              { value: 'assessment', label: 'Assessment' },
              { value: 'jobParsing', label: 'Job Parsing' },
            ]}
            value={formData.template_type}
            onChange={(value) => {
              if (value) {
                const type = value as 'resume' | 'assessment' | 'jobParsing';
                setFormData({
                  ...formData,
                  template_type: type,
                  template_content: DEFAULT_TEMPLATES[type],
                  variables: TEMPLATE_VARIABLES[type],
                });
              }
            }}
            disabled={!!editingId}
          />

          {/* Description */}
          <TextInput
            label="Description (Optional)"
            placeholder="What is this template for?"
            value={formData.description}
            onChange={(e) =>
              setFormData({
                ...formData,
                description: e.currentTarget.value,
              })
            }
          />

          {/* Available Variables */}
          <div>
            <Text size="sm" fw={500} mb="xs">
              Available Variables
            </Text>
            <Text size="xs" c="dimmed">
              Use these placeholders in your template: <code>{TEMPLATE_VARIABLES[formData.template_type].map((v) => `{${v}}`).join(', ')}</code>
            </Text>
          </div>

          {/* Template Content */}
          <Textarea
            label="Template Content"
            placeholder="Enter your prompt template..."
            value={formData.template_content}
            onChange={(e) =>
              setFormData({
                ...formData,
                template_content: e.currentTarget.value,
              })
            }
            minRows={8}
            styles={{ input: { fontFamily: 'monospace', fontSize: '12px' } }}
          />

          {/* Default Toggle */}
          <Switch
            label="Set as default template for this type"
            checked={formData.is_default}
            onChange={(e) =>
              setFormData({
                ...formData,
                is_default: e.currentTarget.checked,
              })
            }
          />

          {/* Action Buttons */}
          <Group justify="flex-end">
            <Button variant="default" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              loading={createMutation.isPending || updateMutation.isPending}
            >
              {editingId ? 'Update Template' : 'Create Template'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
}
